import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ArrowDownIcon from '../icons/ArrowDownIcon';

interface DropdownOption {
  label: string;
  value: string | number;
}

interface AnimatedDropdownProps {
  options: DropdownOption[];
  value: string | number | undefined;
  onChange: (value: string | number) => void;
  placeholder: string;
  error?: boolean;
}

const popupVariants = {
  hidden: {
    opacity: 0,
    scaleY: 0.6,
    y: -4,
    transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
  },
  visible: {
    opacity: 1,
    scaleY: 1,
    y: 0,
    transition: { duration: 0.25, ease: [0, 0, 0.2, 1] },
  },
};

export default function AnimatedDropdown({
  options,
  value,
  onChange,
  placeholder,
  error,
}: AnimatedDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`
          w-full h-12 px-4 pr-10 rounded-lg border text-sm text-left bg-white
          focus:outline-none transition-colors duration-200 cursor-pointer
          ${isOpen ? 'border-[#01A7C8]' : error ? 'border-red-500' : 'border-[#A9B7BE]'}
        `}
      >
        <span className={selectedOption ? 'text-[#1B2831]' : 'text-[#4D5762]'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <motion.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          >
            <ArrowDownIcon fill={isOpen ? '#01A7C8' : '#4D5762'} />
          </motion.div>
        </div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.ul
            variants={popupVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            style={{ transformOrigin: 'top center' }}
            className="absolute z-50 left-0 right-0 mt-1 bg-white border border-[#A9B7BE] rounded-xl overflow-hidden shadow-lg"
          >
            {options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <li key={opt.value}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setIsOpen(false);
                    }}
                    className={`
                      w-full text-left px-4 py-3 text-sm cursor-pointer
                      transition-colors duration-150
                      ${isSelected ? 'text-[#01A7C8] font-semibold bg-[#E8F8FB]' : 'text-[#1B2831]'}
                      hover:bg-[#F0FAFB]
                      active:bg-[#E0F4F7]
                    `}
                  >
                    {opt.label}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
